import React, { useState, useEffect, useCallback, ChangeEvent, KeyboardEvent } from 'react';
import type { CommunityType, CommunitiesSearch } from '../../../apis/community/community.types';
import CommunityForm from '../Identifiers/taskCommunityForm';
import { DEFAULT_PAGE_SIZE } from '../../../constants/constants';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import Drawer from '../../Common/Drawer/Drawer';
import Icon from '../../Common/Icon';
import Notification from '../../../components/Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import { getDataTableSortFields, getSearchParamSortFields } from '../../../helpers/sortHelper';
import { getNullableOnChangeValue } from '../../../helpers/multiselect';
import { useSearchCommunities, useSaveCommunity } from '../../../apis/community';

type CommunityProps = {
    subscriptionId: number;
    onClose: () => void;
};

function Community({ subscriptionId, onClose }: CommunityProps) {
    const newCommunity: CommunityType = {
        id: 0,
        name: '',
        isActive: true,
        subscriptionId,
        createdDate: new Date(),
        createdByUserId: 0,
    };

    // State
    const [communities, setCommunities] = useState<CommunityType[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [selectedCommunity, setSelectedCommunity] = useState<CommunityType>(newCommunity);
    const [editCommunity, setEditCommunity] = useState<CommunityType>(newCommunity);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [isFormValid, setIsFormValid] = useState<boolean>(true);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [showInactiveWarning, setShowInactiveWarning] = useState<boolean>(false);
    const [name, setName] = useState<string>('');
    const [searchParams, setSearchParams] = useState<CommunitiesSearch>({
        subscriptionId,
        isActive: true,
        pageSize: DEFAULT_PAGE_SIZE,
        pageNumber: 1,
        sortBy: 'name',
        sortDir: 'asc',
        name: '',
    });

    // API hooks
    const { data: searchData, isLoading: isQueryLoading, refetch: refetchCommunities } = useSearchCommunities(searchParams);

    const saveCommunityMutation = useSaveCommunity((results) => {
        Notification('success', 'Save Successful', 'Community saved successfully');
        setDrawerOpen(false);
        setSearchParams((prev) => ({ ...prev, pageNumber: 1 }));
        // refetchCommunities will be triggered by query invalidation
    });

    // Effect: update communities when searchData changes
    useEffect(() => {
        if (searchData) {
            setCommunities(searchData.results as CommunityType[]);
            setTotalCount(searchData.totalCount);
        }
    }, [searchData]);

    // Effect: update loading state
    useEffect(() => {
        setIsFetching(isQueryLoading);
    }, [isQueryLoading]);

    // Save handler
    const save = useCallback(() => {
        setIsSaving(true);
        // Add required fields for API
        const payload: CommunityType = {
            ...editCommunity,
            createdDate: editCommunity.createdDate || new Date(),
            createdByUserId: editCommunity.createdByUserId || 0,
        };
        saveCommunityMutation.mutate(payload, {
            onError: (error: any) => {
                setErrorMessage(error.message);
                Notification('error', 'An Error Occurred', error.message);
                setIsSaving(false);
            },
            onSettled: () => {
                setIsSaving(false);
            },
        });
    }, [editCommunity, saveCommunityMutation]);

    // Search trigger (for filters, etc)
    const triggerSearch = useCallback(() => {
        refetchCommunities();
    }, [refetchCommunities]);

    // Handlers
    const filterNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    };

    const keyUpName = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            setSearchParams((sp) => ({ ...sp, name: name }));
        }
    };

    const onSearchBlur = () => {
        setSearchParams((sp) => ({ ...sp, name: name }));
    };

    const onActiveChange = (e: any) => {
        const selectedValue = getNullableOnChangeValue(e);
        setSearchParams((prev) => ({ ...prev, isActive: selectedValue }));
    };

    const onPageChange = (e: PaginatorPageChangeEvent) => {
        setSearchParams((prev) => ({ ...prev, pageNumber: e.page + 1 }));
    };

    const onAddNew = () => {
        setEditCommunity(newCommunity);
        setDrawerOpen(true);
        setErrorMessage('');
        setIsFormValid(true);
        setShowInactiveWarning(false);
    };

    const onFormChange = (name: keyof CommunityType, value: any) => {
        if (name === 'isActive' && value === false && editCommunity.id !== 0) {
            setShowInactiveWarning(true);
        } else {
            setShowInactiveWarning(false);
        }
        setEditCommunity((prev) => ({ ...prev, [name]: value }));
    };

    const saveClick = () => {
        if (!editCommunity.name || editCommunity.name.trim().length < 1) {
            setIsFormValid(false);
            return;
        }
        setIsFormValid(true);
        save();
    };

    const cancelClick = () => {
        setDrawerOpen(false);
    };

    const selectCommunity = (e: { value: CommunityType }) => {
        const newValue = e.value || selectedCommunity;
        setSelectedCommunity(newValue);
        setDrawerOpen(true);
        setIsFormValid(true);
        setEditCommunity(newValue);
        setShowInactiveWarning(false);
    };

    const actionTemplate = (rowData: CommunityType) => {
        if (rowData.isActive) {
            return (
                <div className='text-success text-center'>
                    <Icon name='check-circle' />
                </div>
            );
        } else {
            return (
                <div className='text-danger text-center'>
                    <Icon name='times-circle' />
                </div>
            );
        }
    };

    const onSort = ({ sortField, sortOrder }: { sortField: string; sortOrder: any }) => {
        const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);
        if (sortBy !== searchParams.sortBy || sortDir !== searchParams.sortDir) {
            setSearchParams((prev) => ({ ...prev, sortBy, sortDir }));
        }
    };

    // Render
    const activeOptions = [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
        { label: 'All', value: null },
    ];

    const activeFilter = (
        <Dropdown
            className='ui-column-filter'
            value={searchParams.isActive}
            options={activeOptions}
            optionLabel='label'
            optionValue='value'
            onChange={onActiveChange}
            placeholder='Select an option'
        />
    );

    const nameFilterElement = (
        <input
            type='text'
            className='p-inputtext'
            placeholder='Filter by Name'
            size={50}
            onBlur={onSearchBlur}
            onChange={filterNameChange}
            onKeyUp={keyUpName}
            value={name}
        />
    );

    const { sortField, sortOrder } = getDataTableSortFields(searchParams);

    return (
        <div>
            {drawerOpen ? (
                <Drawer isWide={false} title='Community' onSaveClick={saveClick} onCancelClick={cancelClick}>
                    <CommunityForm
                        community={editCommunity}
                        onFormChange={onFormChange}
                        isFormValid={isFormValid}
                        showWarning={showInactiveWarning}
                        onSubmit={saveClick}
                    />
                </Drawer>
            ) : null}

            <LoadingIndicator isLoading={isFetching || isSaving} hideWhileLoading={true}>
                <div>
                    <div className='identifier-header'>
                        <div className='title'>Community</div>
                        <div className='close'>
                            <button type='button' onClick={onClose}>
                                <Icon name='times' />
                            </button>
                        </div>
                    </div>
                    <div className='identifier-add-new'>
                        <div className='col-sm-12'>
                            <button type='button' className='btn btn-primary float-end' onClick={onAddNew}>
                                <span className='me-2'>Add New</span>
                                <Icon name='plus-circle' />
                            </button>
                        </div>
                    </div>
                    <div className='identifier-results-table'>
                        <DataTable
                            filterDisplay='row'
                            className='p-datatable-sm p-datatable-striped'
                            value={communities}
                            selectionMode='single'
                            selection={selectedCommunity}
                            onSelectionChange={selectCommunity}
                            sortField={sortField}
                            sortOrder={sortOrder as any}
                            onSort={onSort}
                            children={[
                                <Column key={1} field='name' header='Name' sortable={true} filter={true} filterElement={nameFilterElement} />,
                                <Column
                                    key={2}
                                    field='isActive'
                                    header='Active'
                                    sortable
                                    body={actionTemplate}
                                    style={{ width: '120px' }}
                                    filter
                                    filterElement={activeFilter}
                                />,
                            ]}
                        />
                        {totalCount > (searchParams.pageSize || 0) && (
                            <Paginator
                                rows={searchParams.pageSize || 0}
                                totalRecords={totalCount}
                                first={((searchParams.pageNumber || 1) - 1) * (searchParams.pageSize || 0)}
                                onPageChange={onPageChange}
                            />
                        )}
                        <div className='results-count'>Total records: {totalCount}</div>
                    </div>
                </div>
            </LoadingIndicator>
        </div>
    );
}

export default Community;
